import { isLocaleCA } from './locale';

/**
 * Gets the base origin URL for test environments.
 * In test environments, returns the development pilot domain.
 * Otherwise, returns the current window origin.
 *
 * @param isTestEnvironment - Whether the current environment is a test environment
 * @returns The base origin URL
 */
export function getTestEnvironmentBaseOrigin(isTestEnvironment: boolean): string {
    if (!isTestEnvironment) {
        return typeof window !== 'undefined' ? window.location.origin : '';
    }
    const tld = isLocaleCA() ? 'ca' : 'com';
    return `https://development.pilot.petsmart.${tld}`;
}

/**
 * Options for building redirect URLs
 */
export interface RedirectUrlOptions {
    /** Whether the current environment is a test environment */
    isTestEnvironment: boolean;
    /** The path to redirect to (e.g., '/account') */
    path?: string;
    /** Query parameters to include (e.g., { screenToRender: 'resetPasswordRequestCode' }) */
    queryParams?: Record<string, string>;
    /** Whether to base the URL on the current location */
    useCurrentLocation?: boolean;
    /** Pathname to use when useCurrentLocation is true (defaults to window.location.pathname) */
    currentPathname?: string;
    /** Search params to use when useCurrentLocation is true (defaults to window.location.search) */
    currentSearch?: string;
    /** Query parameters to remove from current location */
    removeQueryParams?: string[];
    /** Pathname replacements (e.g., { 'reset-password': 'account' }) */
    pathnameReplacements?: Record<string, string>;
    /** Whether to ensure the pathname ends with '/' */
    ensureTrailingSlash?: boolean;
}

/**
 * Gets a redirect URL with flexible options for different use cases.
 * Can be used for account redirects, password reset flows, and other scenarios.
 *
 * @param options - Configuration options for building the redirect URL
 * @returns The constructed redirect URL
 *
 * @example
 * // Reset password request redirect
 * getRedirectUrl({
 *   isTestEnvironment: false,
 *   path: '/account',
 *   queryParams: { screenToRender: 'resetPasswordRequestCode' }
 * })
 *
 * @example
 * // Token callback redirect based on current location
 * getRedirectUrl({
 *   isTestEnvironment: false,
 *   useCurrentLocation: true,
 *   currentPathname: '/reset-password',
 *   currentSearch: '?code=abc123',
 *   removeQueryParams: ['code'],
 *   pathnameReplacements: { 'reset-password': 'account' },
 *   ensureTrailingSlash: true
 * })
 */
export function getRedirectUrl(options: RedirectUrlOptions): string {
    const {
        isTestEnvironment,
        path,
        queryParams,
        useCurrentLocation = false,
        currentPathname,
        currentSearch,
        removeQueryParams = [],
        pathnameReplacements = {},
        ensureTrailingSlash = false,
    } = options;

    const baseOrigin = getTestEnvironmentBaseOrigin(isTestEnvironment);

    if (useCurrentLocation) {
        // Build URL from current location
        const pathname = currentPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
        const search = currentSearch ?? (typeof window !== 'undefined' ? window.location.search : '');

        let currentUrl: string;
        if (isTestEnvironment) {
            currentUrl = `${baseOrigin}${pathname}${search}`;
        } else if (typeof window !== 'undefined') {
            currentUrl = window.location.href;
        } else {
            // Fallback for server-side rendering when window is not available
            currentUrl = `${baseOrigin}${pathname}${search}`;
        }

        const urlObj = new URL(currentUrl);

        // Remove specified query parameters
        removeQueryParams.forEach(param => urlObj.searchParams.delete(param));

        // Apply pathname replacements
        let finalPathname = urlObj.pathname;
        Object.entries(pathnameReplacements).forEach(([from, to]) => {
            finalPathname = finalPathname.replace(from, to);
        });
        urlObj.pathname = finalPathname;

        // Ensure trailing slash if requested
        if (ensureTrailingSlash && !urlObj.pathname.endsWith('/')) {
            urlObj.pathname += '/';
        }

        // Add any additional query parameters
        if (queryParams) {
            Object.entries(queryParams).forEach(([key, value]) => {
                urlObj.searchParams.set(key, value);
            });
        }

        return urlObj.toString();
    }

    // Build URL from specified path
    const urlObj = new URL(path ?? '/', baseOrigin);

    // Ensure trailing slash if requested, otherwise ensure no trailing slash
    if (ensureTrailingSlash) {
        if (!urlObj.pathname.endsWith('/')) {
            urlObj.pathname += '/';
        }
    } else {
        // Remove trailing slash if present (unless it's the root path)
        if (urlObj.pathname !== '/' && urlObj.pathname.endsWith('/')) {
            urlObj.pathname = urlObj.pathname.slice(0, -1);
        }
    }

    // Add query parameters
    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            urlObj.searchParams.set(key, value);
        });
    }

    return urlObj.toString();
}

/**
 * Gets a redirect URL for the account page with query parameters.
 * This is a convenience wrapper around getRedirectUrl.
 *
 * @param isTestEnvironment - Whether the current environment is a test environment
 * @returns The redirect URL for the account page
 */
export function getAccountRedirectUrl(isTestEnvironment: boolean): string {
    return getRedirectUrl({
        isTestEnvironment,
        path: '/account',
        queryParams: { screenToRender: 'resetPasswordRequestCode' },
        ensureTrailingSlash: true,
    });
}

/**
 * Gets a redirect URL for the account page derived from the current path.
 * This is a convenience wrapper around getRedirectUrl that transforms the current location.
 *
 * @param isTestEnvironment - Whether the current environment is a test environment
 * @param currentPathname - The current pathname (e.g., '/reset-password')
 * @param currentSearch - The current search params (e.g., '?code=...')
 * @param queryParams - Optional query parameters to add to the redirect URL
 * @returns The redirect URL for the account page
 */
export function getAccountRedirectUrlFromPath(
    isTestEnvironment: boolean,
    currentPathname: string,
    currentSearch: string,
    queryParams?: Record<string, string>
): string {
    return getRedirectUrl({
        isTestEnvironment,
        useCurrentLocation: true,
        currentPathname,
        currentSearch,
        removeQueryParams: ['code'],
        pathnameReplacements: { 'reset-password': 'account' },
        ensureTrailingSlash: true,
        queryParams,
    });
}
